// Data access layer — generic CRUD over the Supabase "document" tables.
//
// Every entity table has shape: { id text, doc jsonb, company_id uuid, created_at, updated_at }.
// This layer maps between that row shape and the plain JS objects the app uses
// ({ id, ...fields }), and auto-injects the current user's company_id on insert
// so multi-tenant isolation "just works" (RLS enforces it server-side too).
//
// NOTE (Phase 1): built and tested standalone. NOT yet wired into the app's
// state — the UI still reads/writes localStorage. Data migration is Phase 2.
import { supabase } from "./supabaseClient";
import { getMyMembership } from "./auth";

// ── STALE-TABLE AUDIT (P4-CC18 Phase 0) — READ BEFORE CALLING list()/entity()
// ON ANYTHING BELOW. Every function in this file assumes the Phase-0 doc-store
// shape in the header comment above (id, doc jsonb, company_id, timestamps).
// Most of the tables below no longer have that shape. Three ways that goes
// wrong, worst first:
//
//   CATEGORY A — the doc column is still there, ALONGSIDE real typed columns
//   added later (additive ALTER TABLE, never dropped — e.g. 20260629141039_
//   products_catalogue.sql's own "Promotes the Phase-0 products doc-store...
//   Additive + idempotent"). list() SUCCEEDS, no error at all, and
//   toRecord()'s `{ id: row.id, ...row.doc }` silently returns every REAL
//   field as undefined — the actual data lives in typed columns this file
//   never reads, and doc is stale/empty. The most dangerous category: it
//   looks like it worked.
//     customers, products
//
//   CATEGORY B — the doc-store stub was DROPPED (cascade) and replaced by a
//   typed table with NO doc column at all (see each migration's own
//   `drop table if exists public.<table>`, e.g. 20260706120000_expenses.sql).
//   list() THROWS — "column doc does not exist" (42703) — loudly and
//   immediately. Safer than category A only because it fails instead of
//   lying.
//     invoices, quotations, expenses
//     accounts  -> 20260815120000_cc26a_chart_of_accounts.sql:94 drops the
//                  stub and creates the real chart of accounts.
//     journals  -> 20260816120000_cc26b_journals.sql:114 does the same for the
//                  general ledger, with journal_lines alongside it.
//
//   ⚠️ CATEGORY B IS ONLY LOUD ON FIVE OF THE SIX FUNCTIONS, AND THE SIXTH IS
//   THE DESTRUCTIVE ONE. list(), getOne(), insert(), update() and insertMany()
//   all name `doc`, so they throw 42703 before touching a row. remove() DOES
//   NOT — it is a bare .delete().eq("id", id), so it reaches the real typed
//   table and does exactly what it says. That hole exists for every category B
//   key; for accounts and journals it is pointed at the statutory ledger.
//   NOTHING IN THIS FILE SHOULD BE CALLED ON EITHER KEY. What stops a stray
//   call is the database, not this layer:
//     - RLS: both tables are apply_company_rls_rbac(..., 'accounts'), so the
//       caller needs can_write('accounts') to delete anything at all;
//     - trg_50_system_lock refuses to delete a system account (42501), and
//       journal_lines_account_same_company_fkey refuses (23503) to delete an
//       account that has entries;
//     - trg_60_posted_lock refuses to delete a POSTED journal (42501).
//   A DRAFT journal is covered by none of those: remove(TABLES.journals, id)
//   would delete it, and journal_lines cascades away with it. The real
//   entry points are src/lib/chartOfAccounts.js and the post_* / reverse_*
//   functions — never this one.
//
//   CATEGORY C — the Phase-0 stub survives completely untouched: still
//   exactly { id text, doc jsonb, company_id, created_at, updated_at },
//   RBAC'd, never read or written by anything real. list() "works" (no
//   error) but returns nothing meaningful, because nothing has ever been
//   inserted through it. Most of these are simply dormant — earmarked for a
//   real future migration onto a typed table under this SAME name (orders —
//   see docs/roadmap/README.md's "Migrate Orders + transactions onto
//   Supabase", which names this exact CRUD layer as groundwork already
//   built, not a rebuild; tasks, bankAccounts, transactions, calEvents,
//   payroll, staff, shifts, projects — all still 100% usePersistentState in
//   MsquaredApp.jsx, labelled browser-only previews since CC17).
//
//   ⚠️ accounts AND journals WERE LISTED HERE AND ARE NOT ANY MORE. CC26a and
//   CC26b moved both to category B above, and the "still 100%
//   usePersistentState" claim is false for both:
//     - journals has no usePersistentState at all — the state was removed from
//       MsquaredApp.jsx (P4-CC26d-0-fix) when the Journal Entries tab became a
//       placeholder. Nothing in src/ reads or writes the array.
//     - accounts still has one, but it is an orphan. The Chart of Accounts tab
//       reads public.accounts through src/lib/chartOfAccounts.js (P4-CC26d-0)
//       and nothing renders from the localStorage array. The key is kept only
//       so that anything a user typed before CC26d-0 is preserved rather than
//       silently dropped.
//
//   TWO are actively MISLEADING rather than merely dormant — a
//   real, active, DIFFERENTLY-NAMED table has already superseded the
//   concept, so list() on the key below returns an empty, irrelevant stub
//   while the real feature lives entirely elsewhere:
//     inventory -> the real feature is stock_items/stock_movements
//                  (20260630194946_inventory.sql) — a different schema
//                  under different table names. This key does not point at
//                  "inventory" in any meaningful sense.
//     business  -> the real feature is business_settings
//                  (20260701195725_business_settings.sql) — a wholly
//                  separate table. Not "business settings under an old
//                  name" — simply unused.
//
//   vatReturns was in category C until P4-CC18 (20260805120000_vat_returns.sql)
//   replaced the stub with a real, typed, RBAC'd table with a filed-return
//   lock — see src/lib/vat.js. Removed from the map below for exactly that
//   reason: it is no longer a generic doc-store table this layer should ever
//   touch, and list()/entity() on it would now throw the same way category B
//   does.
//
//   accounts and journals now meet that exact test, and are DELIBERATELY still
//   in the map rather than overlooked: removing a key changes behaviour, and
//   P4-CC26d-0-fix2 was a documentation change. Whoever picks up CC26d-1
//   should decide whether they follow the vatReturns precedent and drop both
//   keys — that is the change that would make remove() unreachable by name
//   instead of merely warned about.
//
// Maps the app's localStorage keys -> Postgres table names (snake_case).
export const TABLES = {
  customers: "customers",
  products: "products",
  invoices: "invoices",
  orders: "orders",
  quotations: "quotations",
  inventory: "inventory",
  expenses: "expenses",
  tasks: "tasks",
  bankAccounts: "bank_accounts",
  transactions: "transactions",
  calEvents: "cal_events",
  accounts: "accounts",
  journals: "journals",
  payroll: "payroll",
  staff: "staff",
  shifts: "shifts",
  projects: "projects",
  business: "business",
};

let _companyId = null;

// Resolve (and cache) the current user's company id. Cleared on auth change.
export async function currentCompanyId() {
  if (_companyId) return _companyId;
  const m = await getMyMembership();
  _companyId = m?.company_id || null;
  return _companyId;
}

export function clearCompanyCache() {
  _companyId = null;
}

function requireClient() {
  if (!supabase) throw new Error("Supabase is not configured (check .env).");
  return supabase;
}

const toRecord = (row) => (row ? { id: row.id, ...row.doc } : null);

// List all rows for a table (RLS scopes to the user's company), newest first.
export async function list(table) {
  const client = requireClient();
  const { data, error } = await client
    .from(table)
    .select("id, doc, created_at")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data || []).map(toRecord);
}

export async function getOne(table, id) {
  const client = requireClient();
  const { data, error } = await client.from(table).select("id, doc").eq("id", id).maybeSingle();
  if (error) throw error;
  return toRecord(data);
}

// Insert one object. The app may supply its own `id` (uid()); otherwise the DB makes one.
export async function insert(table, obj) {
  const client = requireClient();
  const company_id = await currentCompanyId();
  if (!company_id) throw new Error("No company for current user — cannot insert.");
  const { id, ...doc } = obj || {};
  const row = { doc, company_id, ...(id ? { id } : {}) };
  const { data, error } = await client.from(table).insert(row).select("id, doc").single();
  if (error) throw error;
  return toRecord(data);
}

// Replace a row's document wholesale (matches the app's "save whole object" pattern).
export async function update(table, id, obj) {
  const client = requireClient();
  const { id: _ignore, ...doc } = obj || {};
  const { data, error } = await client.from(table).update({ doc }).eq("id", id).select("id, doc").single();
  if (error) throw error;
  return toRecord(data);
}

export async function remove(table, id) {
  const client = requireClient();
  const { error } = await client.from(table).delete().eq("id", id);
  if (error) throw error;
}

// Bulk insert (used by the Phase 2 migration to push localStorage data up).
export async function insertMany(table, objs) {
  const client = requireClient();
  if (!objs || objs.length === 0) return [];
  const company_id = await currentCompanyId();
  if (!company_id) throw new Error("No company for current user — cannot insert.");
  const rows = objs.map((o) => {
    const { id, ...doc } = o || {};
    return { doc, company_id, ...(id ? { id } : {}) };
  });
  const { data, error } = await client.from(table).insert(rows).select("id, doc");
  if (error) throw error;
  return (data || []).map(toRecord);
}

// Convenience: a CRUD handle bound to one table.
export function entity(table) {
  return {
    list: () => list(table),
    get: (id) => getOne(table, id),
    insert: (obj) => insert(table, obj),
    update: (id, obj) => update(table, id, obj),
    remove: (id) => remove(table, id),
    insertMany: (objs) => insertMany(table, objs),
  };
}
